import * as React from 'react';

import { enumScannerStatus } from '@rematter/frontend-lib-api-client';
import {
  Badge,
  Button,
  ConfirmPopoverButton,
  DialogClosingConfirmation,
  EntityDetails,
  LocalizedDate,
  ThemeColor,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { ArrowLeft, Trash2 } from 'lucide-react';

import { scannerDeleteAtom } from '../../../_graphql/mutationScannerDelete';
import { scannerUpdateAtom } from '../../../_graphql/mutationScannerUpdate';
import { useScannerEventsSubscription } from '../../../_graphql/subscriptionScannerEvents';
import * as scannerState from '../../../_state/scannerDetail.state';
import {
  $discardScannerBaseline,
  $hasUnsavedScannerChanges,
  $resetScannerFormToScanner,
  ScannerFormState,
} from '../../../_state/scannerForm.state';
import { useSettingsLocation } from '../../../_state/settingsModule.state';
import { usePopulateScannerForm } from '../../../hooks/usePopulateScannerForm';
import { useTranslations } from '../../../i18n/useTranslations';
import type { ScannerStatus } from '../../../types/sidecar';

import { ScannerTabInfo } from './ScannerTabInfo';
import { ScannerTabTest } from './ScannerTabTest';

import { useHardwareSidecarConnection } from '~/shared/providers/libp2p/useHardwareSidecarConnection';
import { useHardwarePresenceMembers } from '~/shared/providers/presence';

const STATUS_COLORS: Record<ScannerStatus, ThemeColor> = {
  [enumScannerStatus.Discovered]: 'warning',
};

type ScannerDetailTab = 'info' | 'test';

export const ScannerDetailView = () => {
  const t = useTranslations();
  const toast = useToast();
  const [, setLocation] = useSettingsLocation();

  const [currentTab, setCurrentTab] = React.useState<ScannerDetailTab>('info');
  const [isUpdating, setIsUpdating] = React.useState(false);
  const [pendingTabChange, setPendingTabChange] =
    React.useState<ScannerDetailTab | null>(null);

  const hasUnsavedChanges = useAtomValue($hasUnsavedScannerChanges);
  const [, resetForm] = useAtom($resetScannerFormToScanner);
  const [discardBaseline, setDiscardBaseline] = useAtom(
    $discardScannerBaseline,
  );

  const deleteScanner = useSetAtom(scannerDeleteAtom({}));
  const updateScanner = useSetAtom(scannerUpdateAtom({}));

  const sidecarId = useAtomValue(scannerState.$scannerSidecarId);
  const scannerId = useAtomValue(scannerState.$scannerId);

  const {
    data: scanner,
    loadingState,
    error,
    refetch,
  } = useAtomValue(scannerState.$remoteScannerDetail);

  const presenceMembers = useHardwarePresenceMembers();
  const { connection } = useHardwareSidecarConnection({
    sidecar: scanner?.sidecarDetail,
  });

  usePopulateScannerForm({ scanner });

  useScannerEventsSubscription(undefined, {
    onEvent: (event) => {
      if (event?.id === scannerId) {
        refetch?.();
      }
    },
  });

  const handleBack = React.useCallback(() => {
    setLocation('/hardware');
  }, [setLocation]);

  const handleDiscard = React.useCallback(() => {
    resetForm();
    toast.success(t.hardwareView.scannerDetailView.toast.discarded);
  }, [resetForm, toast, t]);

  const handleSaveUpdates = React.useCallback(
    async (data?: ScannerFormState) => {
      if (!scannerId || !data) return;

      try {
        setIsUpdating(true);
        await updateScanner({
          id: scannerId,
          name: data.name.trim(),
        });

        setDiscardBaseline(data);
        toast.success(t.hardwareView.scannerDetailView.toast.updated);
        refetch?.();
      } catch (err) {
        toast.error(
          t.hardwareView.scannerDetailView.toast.updateError(
            err instanceof Error
              ? err.message
              : t.hardwareView.common.unknownError,
          ),
        );
      } finally {
        setIsUpdating(false);
      }
    },
    [scannerId, updateScanner, setDiscardBaseline, toast, t, refetch],
  );

  const handleBeforeChangeTab = React.useCallback(
    (newTab: string) => {
      if (currentTab === 'info' && hasUnsavedChanges) {
        setPendingTabChange(newTab as ScannerDetailTab);
        return false;
      }
      return true;
    },
    [currentTab, hasUnsavedChanges],
  );

  const handleConfirmTabChange = React.useCallback(() => {
    if (pendingTabChange && discardBaseline) {
      resetForm();
      setCurrentTab(pendingTabChange);
    }
    setPendingTabChange(null);
  }, [pendingTabChange, discardBaseline, resetForm]);

  const handleCancelTabChange = React.useCallback(() => {
    setPendingTabChange(null);
  }, []);

  const handleDelete = React.useCallback(async () => {
    if (!scannerId) return;

    try {
      await deleteScanner({ id: scannerId });
      toast.success(t.hardwareView.scannerDetailView.messages.deleteSuccess);
      setLocation('/hardware');
    } catch (err) {
      toast.error(
        t.hardwareView.scannerDetailView.messages.deleteError(
          err instanceof Error
            ? err.message
            : t.hardwareView.common.unknownError,
        ),
      );
      throw err;
    }
  }, [scannerId, deleteScanner, toast, setLocation, t]);

  if (loadingState.isInitialLoading && !scanner) {
    return (
      <div className="@ScannerDetailView flex h-full flex-col">
        <div className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-4 w-1/4 rounded bg-neutral/20" />
            <div className="h-8 w-1/3 rounded bg-neutral/20" />
            <div className="h-32 rounded bg-neutral/20" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !scanner || !scannerId || !sidecarId) {
    return (
      <div className="@ScannerDetailView flex h-full flex-col p-6">
        <div className="text-failure">
          {t.hardwareView.common.errorLabel}:{' '}
          {error?.message ??
            t.hardwareView.scannerDetailView.messages.scannerNotFound}
        </div>
        <Button variant="outline" onPress={handleBack} className="mt-4 w-fit">
          <ArrowLeft className="mr-2 size-4" />
          {t.hardwareView.scannerDetailView.actions.backToHardware}
        </Button>
      </div>
    );
  }

  const scannerStatus = scanner.status as ScannerStatus;
  const isOnline = presenceMembers.has(scanner.id);

  return (
    <div className="@ScannerDetailView flex h-full flex-col">
      <EntityDetails
        className="relative"
        onChangeTab={(tab) => setCurrentTab(tab as ScannerDetailTab)}
        onBeforeChangeTab={handleBeforeChangeTab}
        currentTab={currentTab}
        onBackPressed={handleBack}
        primaryTitle={scanner.name}
        headerActions={
          <ConfirmPopoverButton
            onConfirm={handleDelete}
            message={
              t.hardwareView.scannerDetailView.messages.deleteConfirmation
            }
            confirmLabel={t.hardwareView.common.confirmDelete}
            cancelLabel={t.hardwareView.common.cancel}
            buttonProps={{
              variant: 'outline',
              color: 'failure',
              icon: <Trash2 className="size-4" />,
            }}
          >
            {t.hardwareView.scannerDetailView.actions.delete}
          </ConfirmPopoverButton>
        }
        subtitle={
          <div className="flex items-center gap-2">
            <Badge color={STATUS_COLORS[scannerStatus] ?? 'warning'}>
              {
                t.hardwareView.scannersPanel.statusLabels[
                  scannerStatus as keyof typeof t.hardwareView.scannersPanel.statusLabels
                ]
              }
            </Badge>
            <Badge color={isOnline ? 'success' : 'neutral'}>
              {isOnline
                ? t.hardwareView.scannerDetailView.presenceLabels.online
                : t.hardwareView.scannerDetailView.presenceLabels.offline}
            </Badge>
            {connection?.connected && connection.type === 'direct' && (
              <Badge color="primary">
                {t.hardwareView.scannerDetailView.connectionLabels.direct}
              </Badge>
            )}
            {connection?.connected && connection.type === 'relayed' && (
              <Badge color="neutral">
                {t.hardwareView.scannerDetailView.connectionLabels.relayed}
              </Badge>
            )}
            {!connection?.connected && (
              <Badge color="warning">
                {t.hardwareView.scannerDetailView.connectionLabels.disconnected}
              </Badge>
            )}
            <span className="text-neutral/60">{scanner.driverType}</span>
            <span className="text-neutral/60">
              {'·'} {t.hardwareView.scannerDetailView.fields.created}{' '}
              <LocalizedDate date={scanner.timestamps.createdAt} />
            </span>
          </div>
        }
        tabs={{
          info: {
            title: t.hardwareView.scannerDetailView.tabs.info,
            content: () => (
              <ScannerTabInfo
                scanner={scanner}
                scannerId={scannerId}
                onDiscard={handleDiscard}
                onSaveUpdates={handleSaveUpdates}
                isSaving={isUpdating}
              />
            ),
          },
          test: {
            title: t.hardwareView.scannerDetailView.tabs.test,
            content: () => (
              <ScannerTabTest
                scannerId={scannerId}
                sidecarId={sidecarId}
                sidecarDetail={scanner.sidecarDetail}
              />
            ),
          },
        }}
      />
      <DialogClosingConfirmation
        isOpen={!!pendingTabChange}
        message={t.hardwareView.scannerDetailView.messages.unsavedDataMessage}
        onCancel={handleCancelTabChange}
        onDiscard={handleConfirmTabChange}
      />
    </div>
  );
};
